import {
    useEffect,
    useState,
    useRef,
    useImperativeHandle,
    forwardRef,
    useCallback,
    memo,
} from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"

type AnimationKeys = "mystery_box"
const assets_path_base = "../../../assets/animations/"

// 取得指定動畫 key 對應的 webp 圖片資源
function getAnimationWebps(animation_key: AnimationKeys): string[] {
    const webps = import.meta.glob(`../../../assets/animations/**/*.webp`, {
        eager: true,
        query: "?url",
        import: "default",
    })

    const webps_array = (
        Object.entries(webps)
            .map(([key, value]) => {
                const path = key.replace(assets_path_base, "")
                if (path.startsWith(animation_key)) {
                    const frameNumber = path.replace(
                        new RegExp(`^${animation_key}/(\\d+)\\.webp$`),
                        (_, p1) => p1,
                    )
                    return [Number(frameNumber), value as string]
                }
                return null
            })
            .filter(Boolean) as [number, string][]
    )
        .sort((a, b) => a[0] - b[0])
        .map(([, value]) => value)

    return webps_array
}

export type WebpAnimationHandle = {
    move: (toProgress: number) => Promise<void>
    set: (toProgress: number) => void
    moveStart: () => Promise<void>
    moveEnd: () => Promise<void>
}

const AnimationFrameContainer = styled.div``
type WebpAnimationProps = ExtractStyledProps<typeof AnimationFrameContainer> & {
    animationKey: AnimationKeys
    frameDuration?: number // 單位：毫秒，預設 30ms/frame
}

const WebpAnimation = forwardRef<WebpAnimationHandle, WebpAnimationProps>(
    ({ animationKey, frameDuration = 30, ...props }, ref) => {
        const webps = getAnimationWebps(animationKey)
        const [progress, setProgress] = useState(0)
        const [isAnimating, setIsAnimating] = useState(false)
        const timeoutRef = useRef<NodeJS.Timeout | null>(null)

        const minProgress = 0
        const maxProgress = 100
        const totalFrames = webps.length

        // 清除計時器
        const clearFrameTimer = useCallback(() => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }, [])

        // 進度轉幀資源路徑
        const progressToFrameSrc = useCallback(
            (progressValue: number) => {
                const clampedProgress = Math.max(0, Math.min(100, progressValue))
                // 這裡確保 frameIndex 不會超過最大 index
                const frameIndex = Math.min(
                    Math.floor((clampedProgress / 100) * totalFrames),
                    totalFrames - 1,
                )
                return webps[frameIndex] ?? webps[0]
            },
            [webps, totalFrames],
        )

        // 設置進度
        const setAnimationProgress = useCallback(
            (progressValue: number) => {
                const clampedProgress = Math.max(minProgress, Math.min(maxProgress, progressValue))
                setProgress(clampedProgress)
            },
            [minProgress, maxProgress],
        )

        // 動畫播放到指定進度
        const animationPromiseRef = useRef<Promise<void> | null>(null)
        const animationResolveRef = useRef<(() => void) | null>(null)

        const animateToProgress = useCallback(
            async (targetProgress: number): Promise<void> => {
                // 若有動畫進行中，等待前一個動畫結束
                if (animationPromiseRef.current) {
                    await animationPromiseRef.current
                }
                // 建立新的動畫 promise
                let resolvePromise: () => void
                const promise = new Promise<void>((resolve) => {
                    resolvePromise = resolve
                })
                animationPromiseRef.current = promise
                animationResolveRef.current = resolvePromise!

                setIsAnimating(true)
                clearFrameTimer()
                const clampedTarget = Math.max(minProgress, Math.min(maxProgress, targetProgress))
                const currentProgress = progress
                if (currentProgress === clampedTarget) {
                    setIsAnimating(false)
                    animationPromiseRef.current = null
                    animationResolveRef.current = null
                    resolvePromise!()
                    return
                }
                const step = currentProgress < clampedTarget ? 1 : -1
                const play = (current: number) => {
                    if (
                        (step > 0 && current >= clampedTarget) ||
                        (step < 0 && current <= clampedTarget)
                    ) {
                        setAnimationProgress(clampedTarget)
                        setIsAnimating(false)
                        animationPromiseRef.current = null
                        animationResolveRef.current = null
                        resolvePromise!()
                        return
                    }
                    const nextProgress = Math.min(
                        Math.max(current + step, minProgress),
                        maxProgress,
                    )
                    setAnimationProgress(nextProgress)
                    timeoutRef.current = setTimeout(() => play(nextProgress), frameDuration)
                }
                play(currentProgress)
                return promise
            },
            [
                clearFrameTimer,
                minProgress,
                maxProgress,
                progress,
                setAnimationProgress,
                frameDuration,
            ],
        )

        // 處理設置進度方法
        const handleSetProgress = useCallback(
            (progressValue: number) => {
                setAnimationProgress(progressValue)
            },
            [setAnimationProgress],
        )

        // 移動到開始
        const handleMoveStart = useCallback(async () => {
            return animateToProgress(minProgress)
        }, [animateToProgress, minProgress])

        // 移動到結束
        const handleMoveEnd = useCallback(async () => {
            return animateToProgress(maxProgress)
        }, [animateToProgress, maxProgress])

        useImperativeHandle(
            ref,
            () => ({
                move: animateToProgress,
                set: handleSetProgress,
                moveStart: handleMoveStart,
                moveEnd: handleMoveEnd,
            }),
            [animateToProgress, handleSetProgress, handleMoveStart, handleMoveEnd],
        )

        // 移除時清理計時器
        useEffect(() => {
            return () => {
                clearFrameTimer()
            }
        }, [clearFrameTimer])

        const currentFrameSrc = progressToFrameSrc(progress)

        return (
            <AnimationFrameContainer {...props}>
                <img
                    css={css`
                        width: 100%;
                        height: auto;
                        display: block;
                    `}
                    src={currentFrameSrc}
                    alt={`progress ${progress}%`}
                />
            </AnimationFrameContainer>
        )
    },
)

export default memo(WebpAnimation)
